import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtTL, fmtInt, fmtDate, fmtDateTime } from "@/lib/format";
import { useAuth, hasRole } from "@/lib/auth";
import { Badge, StatusBadge } from "@/components/StatusBadge";
import {
  Package, AlertTriangle, TrendingUp, FileText, Clock, AlertCircle,
  ShoppingCart, ArrowUpRight, Plus, Boxes, Wallet, BarChart3,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, BarChart, Bar, Legend,
} from "recharts";

const NAVY = "#0B3676";
const NAVY_DARK = "#08285A";
const YELLOW = "#FFE600";
const COLORS = ["#0B3676", "#16A34A", "#F59E0B", "#DC2626", "#8B5CF6", "#06B6D4", "#EC4899", "#84CC16"];

const StatCard = ({ icon: Icon, label, value, hint, variant = "default", testid }) => (
  <div className={`azd-card azd-stat-card ${variant === "accent" ? "azd-accent" : ""} ${variant === "warning" ? "azd-warning" : ""} ${variant === "error" ? "azd-error" : ""} ${variant === "success" ? "azd-success" : ""} p-5`} data-testid={testid}>
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold mb-1">
          {label}
        </div>
        <div className="text-2xl font-extrabold text-[#08285A] truncate">{value}</div>
        {hint && <div className="text-xs text-[#6B7280] mt-1">{hint}</div>}
      </div>
      <div className="w-10 h-10 rounded-lg bg-[#0B3676]/8 flex items-center justify-center shrink-0">
        <Icon size={18} className="text-[#0B3676]" />
      </div>
    </div>
  </div>
);

const QuickAction = ({ to, icon: Icon, label, testid }) => {
  const navigate = useNavigate();
  return (
    <button
      data-testid={testid}
      onClick={() => navigate(to)}
      className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white border border-[#E5E7EB] hover:border-[#0B3676] hover:shadow-sm text-sm font-semibold text-[#08285A] transition-all"
    >
      <Icon size={15} />
      {label}
    </button>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [charts, setCharts] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setSummary(r.data));
    api.get("/dashboard/charts").then((r) => setCharts(r.data));
  }, []);

  if (!summary) {
    return <div className="text-[#6B7280]">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Welcome + quick actions */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#0B3676] font-bold">
            Hoş geldiniz
          </div>
          <h2 className="text-2xl font-extrabold text-[#08285A]">{user?.full_name}</h2>
          <p className="text-sm text-[#6B7280] mt-0.5">
            {fmtDate(new Date())} • Bugün şirketinizin tüm akışını buradan takip edebilirsiniz.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {hasRole(user, "admin", "warehouse") && (
            <QuickAction to="/products/new" icon={Plus} label="Ürün Ekle" testid="qa-product" />
          )}
          {hasRole(user, "admin", "warehouse") && (
            <QuickAction to="/stock/entry" icon={Boxes} label="Stok Girişi" testid="qa-stock-in" />
          )}
          {hasRole(user, "admin", "sales") && (
            <QuickAction to="/quotes/new" icon={FileText} label="Teklif Hazırla" testid="qa-quote" />
          )}
          {hasRole(user, "admin", "sales") && (
            <QuickAction to="/orders/new" icon={ShoppingCart} label="Sipariş Hazırla" testid="qa-order" />
          )}
          {hasRole(user, "admin", "accounting") && (
            <QuickAction to="/payments/new" icon={Wallet} label="Ödeme Girişi" testid="qa-payment" />
          )}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Package} label="Toplam Ürün" value={fmtInt(summary.total_products)}
                  hint="Aktif ürün sayısı" testid="kpi-products" />
        <StatCard icon={Boxes} label="Stok Değeri" value={fmtTL(summary.total_stock_value)}
                  hint="Alış maliyeti üzerinden" variant="accent" testid="kpi-stock-value" />
        <StatCard icon={AlertTriangle} label="Kritik Stok" value={fmtInt(summary.critical_count)}
                  hint="Kritik seviyedeki ürünler" variant="warning" testid="kpi-critical" />
        <StatCard icon={TrendingUp} label="Aylık Satış" value={fmtTL(summary.month_sales)}
                  hint="Bu ay" variant="success" testid="kpi-month-sales" />
        <StatCard icon={FileText} label="Aylık Teklif" value={fmtInt(summary.month_quotes)}
                  hint={`${summary.approved_quotes} onaylı / ${summary.pending_quotes} bekleyen`}
                  testid="kpi-quotes" />
        <StatCard icon={ShoppingCart} label="Bugünkü Sipariş" value={fmtInt(summary.today_orders)}
                  hint="Bugün oluşan" testid="kpi-today-orders" />
        <StatCard icon={AlertCircle} label="Vadesi Geçmiş" value={fmtTL(summary.overdue_total)}
                  hint="Toplam gecikmiş borç" variant="error" testid="kpi-overdue" />
        <StatCard icon={Clock} label="Yakın Vade" value={fmtInt(summary.upcoming_count)}
                  hint="7 gün içinde dolacak" variant="warning" testid="kpi-upcoming" />
      </div>

      {/* Charts */}
      {charts && (
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="azd-card p-5 lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">
                  Aylık Satış
                </div>
                <div className="text-base font-bold text-[#08285A]">Son 6 ay</div>
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer>
                <AreaChart data={charts.monthly_sales}>
                  <defs>
                    <linearGradient id="navy" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={NAVY} stopOpacity={0.5} />
                      <stop offset="100%" stopColor={NAVY} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6B7280" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#6B7280" }}
                         tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                  <Tooltip
                    formatter={(v) => fmtTL(v)}
                    contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB" }}
                  />
                  <Area type="monotone" dataKey="value" stroke={NAVY} strokeWidth={2.5} fill="url(#navy)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="azd-card p-5">
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">
              Kategoriye Göre Satış
            </div>
            <div className="text-base font-bold text-[#08285A] mb-4">Dağılım</div>
            <div className="h-64">
              {charts.category_sales.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-[#6B7280]">
                  Henüz satış verisi yok
                </div>
              ) : (
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={charts.category_sales} dataKey="value" nameKey="name"
                         cx="50%" cy="50%" innerRadius={45} outerRadius={85}>
                      {charts.category_sales.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => fmtTL(v)} />
                    <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="azd-card p-5 lg:col-span-2">
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">
              En Çok Satan Ürünler
            </div>
            <div className="text-base font-bold text-[#08285A] mb-4">İlk 8</div>
            <div className="h-64">
              {charts.top_products.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-[#6B7280]">
                  Henüz satış verisi yok
                </div>
              ) : (
                <ResponsiveContainer>
                  <BarChart data={charts.top_products} layout="vertical" margin={{ left: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis type="number" tick={{ fontSize: 11, fill: "#6B7280" }}
                           tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                    <YAxis type="category" dataKey="product_name" tick={{ fontSize: 10, fill: "#374151" }}
                           width={140} />
                    <Tooltip formatter={(v) => fmtTL(v)} />
                    <Bar dataKey="revenue" fill={NAVY} radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="azd-card p-5">
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">
              Markaya Göre Stok
            </div>
            <div className="text-base font-bold text-[#08285A] mb-4">Stok değeri</div>
            <div className="h-64">
              {charts.brand_stock.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-[#6B7280]">
                  Veri yok
                </div>
              ) : (
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={charts.brand_stock} dataKey="value" nameKey="name"
                         cx="50%" cy="50%" outerRadius={85}>
                      {charts.brand_stock.map((_, i) => (
                        <Cell key={i} fill={COLORS[(i + 2) % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => fmtTL(v)} />
                    <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Recent activity */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="azd-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-bold text-[#08285A]">Son Stok Hareketleri</div>
            <button onClick={() => navigate("/stock/movements")}
                    className="text-[11px] text-[#0B3676] hover:underline font-semibold"
                    data-testid="see-all-movements">
              Tümü →
            </button>
          </div>
          <div className="space-y-2 max-h-80 overflow-y-auto azd-scrollbar">
            {summary.recent_movements.length === 0 ? (
              <div className="text-sm text-[#6B7280] text-center py-6">Hareket yok</div>
            ) : (
              summary.recent_movements.map((m) => (
                <div key={m.id} className="flex items-center gap-3 py-2 border-b border-[#E5E7EB] last:border-0">
                  <div className={`w-1.5 h-8 rounded-full ${m.direction === "in" ? "bg-[#16A34A]" : "bg-[#DC2626]"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-[#111827] truncate">
                      {m.product_name}
                    </div>
                    <div className="text-[11px] text-[#6B7280]">
                      {fmtDateTime(m.created_at)}
                    </div>
                  </div>
                  <div className="text-[13px] font-bold text-[#08285A]">
                    {m.direction === "in" ? "+" : "-"}{m.quantity} {m.unit}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="azd-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-bold text-[#08285A]">Son Siparişler</div>
            <button onClick={() => navigate("/orders")}
                    className="text-[11px] text-[#0B3676] hover:underline font-semibold"
                    data-testid="see-all-orders">
              Tümü →
            </button>
          </div>
          <div className="space-y-2 max-h-80 overflow-y-auto azd-scrollbar">
            {summary.recent_orders.length === 0 ? (
              <div className="text-sm text-[#6B7280] text-center py-6">Sipariş yok</div>
            ) : (
              summary.recent_orders.map((o) => (
                <div key={o.id} className="py-2 border-b border-[#E5E7EB] last:border-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[13px] font-bold text-[#0B3676]">{o.order_no}</div>
                    <StatusBadge status={o.status} />
                  </div>
                  <div className="text-[12px] text-[#6B7280] truncate">{o.company_name}</div>
                  <div className="text-[13px] font-bold text-[#08285A] mt-0.5">
                    {fmtTL(o.total_amount)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="azd-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-bold text-[#08285A]">Son Ödemeler</div>
            <button onClick={() => navigate("/cari/movements")}
                    className="text-[11px] text-[#0B3676] hover:underline font-semibold"
                    data-testid="see-all-payments">
              Tümü →
            </button>
          </div>
          <div className="space-y-2 max-h-80 overflow-y-auto azd-scrollbar">
            {summary.recent_payments.length === 0 ? (
              <div className="text-sm text-[#6B7280] text-center py-6">Ödeme yok</div>
            ) : (
              summary.recent_payments.map((p) => (
                <div key={p.id} className="py-2 border-b border-[#E5E7EB] last:border-0">
                  <div className="text-[13px] font-semibold text-[#111827] truncate">
                    {p.company_name}
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <div className="text-[11px] text-[#6B7280]">{fmtDate(p.receipt_date)}</div>
                    <div className="text-[13px] font-bold text-[#16A34A]">
                      +{fmtTL(p.payment_amount)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
